/**
 * <scent-canvas> — Composite hero image for the new-customer subscription PDP.
 *
 * Listens to two custom events dispatched by the MonthsSubscriptionPicker:
 *   - subscription-picker:colorChanged    → swap applicator layer
 *   - subscription-picker:scentCardUpdated → fill/clear scent slots (Task 6)
 *
 * Each event is treated as a state snapshot; the component re-derives full
 * layer state from event.detail. No diff tracking, no race conditions.
 */
class ScentCanvas extends HTMLElement {
  constructor() {
    super();
    this.selectedColor = (this.dataset.defaultColor || 'black').toLowerCase();

    this.orderedScentSkus = [];
    this.unsubs = [];
  }

  connectedCallback() {
    const subscriptionWidget = document.querySelector('subscription-picker');
    this.hasApplicator = true;
    if (subscriptionWidget.getAttribute('data-refill-product-only') === 'true') {
      this.hasApplicator = false;
      this.setAttribute('data-has-applicator', 'false');
    }
    
    this.applicatorLayers = this.querySelectorAll('.canvas-layer--applicator');
    this.scentLayers = this.querySelectorAll('.canvas-layer--scent');

    // Build SKU → layer map by pairing picker scent cards (in DOM order) with
    // canvas scent layers (in data-scent-index order). Both derive from the
    // same admin-side ordering, so positions align.
    this.scentSkuToLayer = new Map();
    const scentCards = document.querySelectorAll('.scent-selector-grid .scent-card[data-scent-sku]');
    scentCards.forEach((card, index) => {
      const layer = this.querySelector(`.canvas-layer--scent[data-scent-index="${index}"]`);
      if (layer && card.dataset.scentSku) {
        this.scentSkuToLayer.set(card.dataset.scentSku, layer);
      }
    });

    this.allOrderedSkus = scentCards.length > 0 ? Array.from(scentCards).map((c) => c.dataset.scentSku) : [];

    this.applyColor(this.selectedColor);
    this.applyScents([]);

    const onColor = (e) => this.applyColor(e?.detail?.selectedColor, e);
    const onScent = (e) => this.applyScents(e?.detail?.selectedScentSkusOrdered, e);
    window.addEventListener('subscription-picker:colorChanged', onColor);
    window.addEventListener('subscription-picker:scentCardUpdated', onScent);
    this.unsubs.push(
      () => window.removeEventListener('subscription-picker:colorChanged', onColor),
      () => window.removeEventListener('subscription-picker:scentCardUpdated', onScent),
    );
  }

  disconnectedCallback() {
    this.unsubs.forEach((fn) => fn());
    this.unsubs = [];
  }

  applyColor(color) {
    if (!color) return;
    this.selectedColor = color.toLowerCase();
    this.applicatorLayers.forEach((img) => {
      img.hidden = img.dataset.color !== this.selectedColor;
    });
  }

  applyScents(orderedSkus = [], event) {
    // Sync layout attribute with the picker's current required quantity so
    // frequency changes (e.g. monthly→quarterly: 2→4 scents) re-flow the
    // canvas slots instead of keeping the previous layout.
    const pickerRequired = event?.target?.getRequiredScentQuantity?.();
    if (pickerRequired) {
      this.requiredScentQuantity = pickerRequired;
      this.setAttribute('required-scent-quantity', String(pickerRequired));
    }

    // Only When widget is open preset the First two scents
    if (orderedSkus.length == 0 && !!event) {
      orderedSkus = this.allOrderedSkus.slice(0, this.requiredScentQuantity || 2);
    }

    this.orderedScentSkus = orderedSkus;

    // Hide the whole canvas when no scents → gallery falls back to featured_image
    this.hidden = this.orderedScentSkus.length === 0;

    // Drive the parametric slot layout in scent-canvas.css. The host advertises
    // the actual total count; each layer advertises its own index. CSS
    // interpolates left between --slot-start and --slot-end and selects the
    // narrow 2-scent band only when the *actual* count is 2 — not when the
    // subscription minimum is 2 (one-time can grow past that).
    const slotCount = this.orderedScentSkus.length;
    this.style.setProperty('--slot-count', String(slotCount));
    if (slotCount > 0) {
      this.dataset.slotCount = String(slotCount);
    } else {
      delete this.dataset.slotCount;
    }

    // Reset state: hide all source layers, clear any clones from previous calls.
    this.scentLayers.forEach((img) => {
      img.hidden = true;
      delete img.dataset.slot;
      img.style.removeProperty('--slot-index');
    });
    this.querySelectorAll('.canvas-layer--scent-clone').forEach((c) => c.remove());

    // Place a layer for each ordered SKU. First occurrence uses the source
    // <img>; subsequent occurrences (qty > 1 of the same scent, or multiple
    // identical selections) get their own cloned <img> so the same scent
    // can occupy multiple slots simultaneously.
    const usedSources = new Set();
    this.orderedScentSkus.forEach((sku, slotIndex) => {
      const source = this.scentSkuToLayer.get(sku);
      if (!source) return;

      let layer;
      if (!usedSources.has(source)) {
        usedSources.add(source);
        layer = source;
      } else {
        layer = source.cloneNode(false);
        layer.classList.add('canvas-layer--scent-clone');
        this.appendChild(layer);
      }
      layer.hidden = false;
      layer.dataset.slot = String(slotIndex);
      layer.style.setProperty('--slot-index', String(slotIndex));
    });
  }
}

customElements.define('scent-canvas', ScentCanvas);
